import { SearchFilterOptionEnum } from '@components/cockpit/panels/notificationSearch/notificationSearch.types';
import { isEqual } from 'lodash';
import { useReducer } from 'react';

import { NotificationFamilyEnum } from '@/types/data/data.types';

type Action =
  | { type: 'TOGGLE_CATEGORY'; payload: NotificationFamilyEnum }
  | { type: 'TOGGLE_VALUE'; payload: SearchFilterOptionEnum }
  | { type: 'DISPLAY_ALL'; payload: NotificationFamilyEnum }
  | { type: 'DISPLAY_NONE'; payload: NotificationFamilyEnum };

export const availableFilterOptionsByCategory: Record<NotificationFamilyEnum, Set<SearchFilterOptionEnum>> = {
  [NotificationFamilyEnum.LAD]: new Set([
    SearchFilterOptionEnum.PLATFORM,
    SearchFilterOptionEnum.COLLABORATIVE_ID,
    SearchFilterOptionEnum.ID,
    SearchFilterOptionEnum.ZONES,
    SearchFilterOptionEnum.TRACK_SENSOR,
    SearchFilterOptionEnum.WORST_CLASSIFICATION,
    SearchFilterOptionEnum.DETECTION_PERIOD,
    SearchFilterOptionEnum.RELIABILITY,
    SearchFilterOptionEnum.MAX_ALTITUDE_OR_HEIGHT,
    SearchFilterOptionEnum.DETECTION_DURATION,
  ]),
  [NotificationFamilyEnum.PERIMETER]: new Set([
    SearchFilterOptionEnum.PLATFORM,
    SearchFilterOptionEnum.ID,
    SearchFilterOptionEnum.INCIDENT_PERIOD,
  ]),
  [NotificationFamilyEnum.TECHNICAL]: new Set([
    SearchFilterOptionEnum.PLATFORM,
    SearchFilterOptionEnum.ID,
    SearchFilterOptionEnum.INCIDENT_PERIOD,
    SearchFilterOptionEnum.WORST_SENSOR_STATUS,
    SearchFilterOptionEnum.TREATMENT_DURATION,
  ]),
};

export const defaultAvailableFilterOptionsByCategory: Record<NotificationFamilyEnum, Set<SearchFilterOptionEnum>> = {
  [NotificationFamilyEnum.LAD]: new Set([SearchFilterOptionEnum.DETECTION_PERIOD, SearchFilterOptionEnum.RELIABILITY]),
  [NotificationFamilyEnum.PERIMETER]: new Set([
    SearchFilterOptionEnum.PLATFORM,
    SearchFilterOptionEnum.ID,
    SearchFilterOptionEnum.INCIDENT_PERIOD,
  ]),
  [NotificationFamilyEnum.TECHNICAL]: new Set([
    SearchFilterOptionEnum.PLATFORM,
    SearchFilterOptionEnum.ID,
    SearchFilterOptionEnum.WORST_SENSOR_STATUS,
    SearchFilterOptionEnum.INCIDENT_PERIOD,
    SearchFilterOptionEnum.TREATMENT_DURATION,
  ]),
};

const mandatoryFilterOptionsByCategory: Record<NotificationFamilyEnum, Set<SearchFilterOptionEnum>> = {
  [NotificationFamilyEnum.LAD]: new Set([SearchFilterOptionEnum.DETECTION_PERIOD]),
  [NotificationFamilyEnum.PERIMETER]: new Set([SearchFilterOptionEnum.INCIDENT_PERIOD]),
  [NotificationFamilyEnum.TECHNICAL]: new Set([SearchFilterOptionEnum.INCIDENT_PERIOD]),
};

function displayFilterOptionsReducer(
  displayedFilterOptions: Set<SearchFilterOptionEnum>,
  action: Action,
): Set<SearchFilterOptionEnum> {
  switch (action.type) {
    case 'TOGGLE_CATEGORY':
      return defaultAvailableFilterOptionsByCategory[action.payload];
    case 'TOGGLE_VALUE': {
      const newSet = new Set(displayedFilterOptions);
      newSet.has(action.payload) ? newSet.delete(action.payload) : newSet.add(action.payload);
      return newSet;
    }
    case 'DISPLAY_ALL':
      return new Set(availableFilterOptionsByCategory[action.payload]);
    case 'DISPLAY_NONE':
      return mandatoryFilterOptionsByCategory[action.payload];
    default:
      return displayedFilterOptions;
  }
}

export interface DisplaySearchFilterOptionType {
  displayedFilterOptions: Set<SearchFilterOptionEnum>;
  isAllFilterOptionsDisplayed: (category: NotificationFamilyEnum) => boolean;
  isNoOptionalFilterOptionsDisplayed: (category: NotificationFamilyEnum) => boolean;
  toggleDisplayedFilterOptionCategory: (category?: NotificationFamilyEnum) => void;
  toggleDisplayedFilterOptionValue: (value: SearchFilterOptionEnum) => void;
  displayAllFilterOptions: (category: NotificationFamilyEnum) => void;
  displayNoOptionalFilterOptions: (category: NotificationFamilyEnum) => void;
}

export const useDisplaySearchFilterOption = (category: NotificationFamilyEnum): DisplaySearchFilterOptionType => {
  const [displayedFilterOptions, dispatchDisplayedFilterOptions] = useReducer(
    displayFilterOptionsReducer,
    defaultAvailableFilterOptionsByCategory[category],
  );

  const isAllFilterOptionsDisplayed = (category: NotificationFamilyEnum) =>
    isEqual(displayedFilterOptions, new Set(availableFilterOptionsByCategory[category]));

  const isNoOptionalFilterOptionsDisplayed = (category: NotificationFamilyEnum) =>
    isEqual(displayedFilterOptions, mandatoryFilterOptionsByCategory[category]);

  const toggleDisplayedFilterOptionCategory = (category: NotificationFamilyEnum = NotificationFamilyEnum.LAD) => {
    dispatchDisplayedFilterOptions({ type: 'TOGGLE_CATEGORY', payload: category });
  };

  const toggleDisplayedFilterOptionValue = (value: SearchFilterOptionEnum) => {
    dispatchDisplayedFilterOptions({ type: 'TOGGLE_VALUE', payload: value });
  };

  const displayAllFilterOptions = (category: NotificationFamilyEnum) => {
    dispatchDisplayedFilterOptions({ type: 'DISPLAY_ALL', payload: category });
  };

  const displayNoOptionalFilterOptions = (category: NotificationFamilyEnum) => {
    dispatchDisplayedFilterOptions({ type: 'DISPLAY_NONE', payload: category });
  };

  return {
    displayedFilterOptions,
    isAllFilterOptionsDisplayed,
    isNoOptionalFilterOptionsDisplayed,
    toggleDisplayedFilterOptionCategory,
    toggleDisplayedFilterOptionValue,
    displayAllFilterOptions,
    displayNoOptionalFilterOptions,
  };
};
